/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-nested-ternary */
import React, { FC, useEffect, useMemo } from 'react';
import { t } from 'i18next';
import { useFormApi } from 'informed';
import cn from 'classnames';
import { Dropdown } from '@ui-modules/informed';
import { IDropdownOption } from '@ui-modules/types';
import { MappedDrive, FacilityVehicle } from '@common/interfaces';
import { notEmptyValidator } from '@common/utils';
import css from '../styles.module.scss';

interface IDriverField {
  driversOptions: IDropdownOption[];
  driversOptionsFull: IDropdownOption[];
  driver?: string | null;
  isEdit: boolean;
  sharedDrive: MappedDrive | null;
  multilegField?: string;
  vehicles: Array<FacilityVehicle>;
  returnTrip?: boolean;
}

const getFieldName = (field: string, returnTrip: boolean, multilegField?: string): string => {
  const tripInfoField = `${returnTrip ? 'return_trip' : 'trip_info'}.${field}`;
  return multilegField
    ? `${multilegField}.${tripInfoField}`
    : returnTrip
    ? `${tripInfoField}_return`
    : tripInfoField;
};

const DriverField: FC<IDriverField> = ({
  driversOptions,
  driversOptionsFull,
  driver,
  isEdit,
  sharedDrive,
  multilegField,
  vehicles,
  returnTrip = false,
}) => {
  const fieldName = getFieldName('driver', returnTrip, multilegField);
  const vehicleFieldName = getFieldName('vehicle', returnTrip, multilegField);

  const formApi = useFormApi();
  const vehicle = formApi.getValue(vehicleFieldName) as IDropdownOption;
  const driverOption = formApi.getValue(fieldName) as IDropdownOption;

  const selectedVehicle = vehicles?.find((v) => v.vehicle_identifier === vehicle?.value);

  const preferredDriver = selectedVehicle?.shared
    ? driversOptions.find((driver) => driver.value === selectedVehicle?.preferred_driver)
    : undefined;

  const defaultValue =
    preferredDriver ||
    (sharedDrive !== null
      ? driversOptionsFull.find((i) => i.value === sharedDrive.driver_id)
      : driversOptions.find((i) => i.value === driver));

  const availableDriversOptions = useMemo(() => {
    const sharedDrivers = vehicles
      .filter((vehicle) => vehicle.shared && vehicle.preferred_driver)
      .map((vehicle) => vehicle.preferred_driver);

    return selectedVehicle?.shared
      ? driversOptions
      : driversOptions.filter((driver) => !sharedDrivers.includes(driver.value));
  }, [selectedVehicle, vehicles, driversOptions]);

  useEffect(() => {
    if (vehicle) {
      if (driverOption) {
        const isSharedDriverSelected = vehicles.some(
          (vehicle) => vehicle.shared && vehicle.preferred_driver === driverOption.value,
        );

        if (isSharedDriverSelected && !selectedVehicle?.shared) {
          formApi.setValue(fieldName, null);
          return;
        }
      }

      if (selectedVehicle?.shared && preferredDriver && isEdit) {
        formApi.setValue(fieldName, preferredDriver);
      }
    }
  }, [vehicle]);

  useEffect(() => {
    if (defaultValue) {
      formApi.setValue(fieldName, defaultValue);
    }
  }, [defaultValue]);

  return (
    <div className={css.field} data-testid="mobility-planner-edit-form-driver">
      <Dropdown
        className={cn('dark-border', 'small-list')}
        defaultValue={defaultValue}
        isDisabled={sharedDrive !== null || preferredDriver !== undefined}
        label={t('common.driver')}
        labelClassName={css['dropdown-label']}
        name={multilegField ? `${multilegField}.driver` : returnTrip ? 'driver_return' : 'driver'}
        options={availableDriversOptions}
        validate={notEmptyValidator}
      />
    </div>
  );
};

export default DriverField;
